import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { Project, ProjectStatus } from '../../types/project';

interface ProjectCardProps {
  project: Project;
  onClick: () => void;
  showOwnerInfo?: boolean;
}

/**
 * Displays a single project card with status badge and metadata
 */
export const ProjectCard: React.FC<ProjectCardProps> = ({ project, onClick, showOwnerInfo = false }) => {
  const [ownerEmail, setOwnerEmail] = useState<string | null>(null);

  // Fetch owner email if needed
  useEffect(() => {
    if (showOwnerInfo && project.ownerId) {
      const fetchOwnerEmail = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'users', project.ownerId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setOwnerEmail(userData.email || 'Unknown');
          }
        } catch (err) {
          console.error('Error fetching owner email:', err);
        }
      };

      fetchOwnerEmail();
    }
  }, [showOwnerInfo, project.ownerId]);
  // Format date to readable string
  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  // Get status badge styling
  const getStatusBadge = (status: ProjectStatus) => {
    const badges: Record<ProjectStatus, { label: string; className: string }> = {
      draft: {
        label: 'Draft',
        className: 'bg-gray-100 text-gray-800',
      },
      research_complete: {
        label: 'Research Complete',
        className: 'bg-blue-100 text-blue-800',
      },
      personas_generated: {
        label: 'Personas Generated',
        className: 'bg-secondary-100 text-purple-800',
      },
      active: {
        label: 'Active',
        className: 'bg-green-100 text-green-800',
      },
      archived: {
        label: 'Archived',
        className: 'bg-yellow-100 text-yellow-800',
      },
    };

    const badge = badges[status];
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
      >
        {badge.label}
      </span>
    );
  };

  return (
    <div
      onClick={onClick}
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-indigo-300 transition-all cursor-pointer group"
    >
      {/* Header with Status Badge */}
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-500 transition-colors line-clamp-1">
          {project.name}
        </h3>
        <div className="flex flex-col gap-1 items-end">
          {getStatusBadge(project.status)}
          {showOwnerInfo && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Shared
            </span>
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 mb-4 line-clamp-2 min-h-[2.5rem]">
        {project.description}
      </p>

      {/* Owner Info */}
      {showOwnerInfo && ownerEmail && (
        <div className="mb-3 flex items-center text-xs text-gray-600">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span>Owner: {ownerEmail}</span>
        </div>
      )}

      {/* Footer with Metadata */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center">
          <svg
            className="w-4 h-4 mr-1"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <span>Created {formatDate(project.createdAt)}</span>
        </div>

        {/* Arrow icon on hover */}
        <svg
          className="w-5 h-5 text-gray-400 group-hover:text-primary-500 group-hover:translate-x-1 transition-all"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 7l5 5m0 0l-5 5m5-5H6"
          />
        </svg>
      </div>
    </div>
  );
};
